// ANGEL FLIGHT — ColorGrade: the COLOR cinematic "lens" (default look).
//
// A small bundle of postprocessing Effects that, fused into ONE EffectPass, glue
// the stylized CC0 / procedural art into a cohesive, pro-reading frame WITHOUT
// touching the underlying lit 3D scene's colour:
//
//   BloomEffect              — glow ONLY on genuine bright/emissive highlights
//                              (muzzle flash, tracers, explosions). Threshold +
//                              smoothing from RENDER.grade keep midtones clean.
//   VignetteEffect           — subtle radial edge darkening (focus the centre).
//   NoiseEffect              — a light, animated film grain (texture/cohesion).
//   ChromaticAberrationEffect— a mild radial colour fringe at the frame edges.
//
// All four are layered into one EffectPass by the pipeline (postprocessing fuses
// them into a single fragment program → one draw). This class only owns the
// effects + their disposal; the pipeline owns the pass/composer lifecycle.
//
// Tone mapping (ACESFilmic + RENDER.grade.toneMappingExposure) is the renderer's
// job, set by RenderPipeline when entering color mode — it is NOT part of this
// grade pass so the HDR scene is mapped before bloom reads its highlights.

import {
  BloomEffect,
  BrightnessContrastEffect,
  ChromaticAberrationEffect,
  Effect,
  HueSaturationEffect,
  NoiseEffect,
  VignetteEffect,
} from 'postprocessing';
import * as THREE from 'three';
import { RENDER } from '../config/render';

export class ColorGrade {
  readonly brightnessContrast: BrightnessContrastEffect;
  readonly hueSaturation: HueSaturationEffect;
  readonly bloom: BloomEffect;
  readonly vignette: VignetteEffect;
  readonly grain: NoiseEffect;
  readonly chromaticAberration: ChromaticAberrationEffect;

  /** The effects to hand to a single EffectPass, in compositing order. */
  readonly effects: readonly Effect[];

  constructor() {
    const g = RENDER.grade;

    // Tonal punch FIRST (before bloom) so the contrast S-curve pushes genuine
    // highlights brighter into the bloom threshold and the dusk frame gains range
    // instead of sitting in one compressed warm midtone band.
    this.brightnessContrast = new BrightnessContrastEffect({
      brightness: g.brightness,
      contrast: g.contrast,
    });
    // Saturation lift gives the warm/cool split (amber key vs blue shadow) real
    // colour without us cranking the light tints into "orange soup".
    this.hueSaturation = new HueSaturationEffect({ saturation: g.saturation });

    // Bloom: SCREEN blend (postprocessing's default for bloom) so it only ADDS
    // light on top of bright pixels. The threshold/smoothing gate it to true
    // highlights — muzzle/tracers/explosions — and leave the lit world alone.
    this.bloom = new BloomEffect({
      intensity: g.bloomIntensity,
      luminanceThreshold: g.bloomThreshold,
      luminanceSmoothing: g.bloomSmoothing,
      radius: g.bloomRadius,
      mipmapBlur: true,
    });

    // Subtle vignette: NORMAL blend, darkness from the grade. `offset` controls
    // how far in the falloff starts; keep it generous so only the corners dip.
    this.vignette = new VignetteEffect({
      offset: 0.3,
      darkness: g.vignette,
    });

    // Film grain: SCREEN-blended animated noise, scaled down by the blend
    // opacity so it reads as fine texture rather than TV static.
    this.grain = new NoiseEffect({ premultiply: true });
    this.grain.blendMode.opacity.value = g.grain;

    // Mild chromatic aberration, modulated radially so it's ~zero in the centre
    // and only fringes toward the frame edges (lens character, not a defect).
    this.chromaticAberration = new ChromaticAberrationEffect({
      offset: new THREE.Vector2(g.chromaticAberration, g.chromaticAberration),
      radialModulation: true,
      modulationOffset: 0.15,
    });

    this.effects = [
      this.brightnessContrast,
      this.hueSaturation,
      this.bloom,
      this.vignette,
      this.grain,
      this.chromaticAberration,
    ];
  }

  /** Free GPU resources for every effect this grade owns. */
  dispose(): void {
    this.brightnessContrast.dispose();
    this.hueSaturation.dispose();
    this.bloom.dispose();
    this.vignette.dispose();
    this.grain.dispose();
    this.chromaticAberration.dispose();
  }
}
